<template>
  <v-container fluid>
    <v-card class="mx-auto" max-width="1200">
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-calendar-clock</v-icon>
        Working Hours & Availability Manager
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Global Diary Start Time -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4">
              <v-card-subtitle class="pa-0 mb-3">
                <v-icon left color="secondary"
                  >mdi-book-open-page-variant</v-icon
                >
                <strong>Global Diary Settings</strong>
              </v-card-subtitle>
              <v-select
                v-model="globalDiaryStart"
                :items="timeOptions"
                item-text="label"
                item-value="value"
                label="Diary Start Time"
                outlined
                dense
                prepend-inner-icon="mdi-book-clock"
                hint="When diary/appointments can start each day"
                persistent-hint
              ></v-select>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4">
              <v-card-subtitle class="pa-0 mb-3">
                <v-icon left color="info">mdi-information</v-icon>
                <strong>Quick Summary</strong>
              </v-card-subtitle>
              <div class="text-body-2">
                <div><strong>Working Days:</strong> {{ enabledDaysCount }}</div>
                <div>
                  <strong>Total Weekly Hours:</strong>
                  {{ totalWeeklyHours }} hours
                </div>
                <div>
                  <strong>Diary Starts:</strong>
                  {{ formatTimeDisplay(globalDiaryStart) }}
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Weekly Working Hours -->
        <v-row>
          <v-col
            v-for="day in workingHours"
            :key="day.name"
            cols="12"
            md="6"
            lg="6"
          >
            <v-card class="day-card" :class="{ 'day-disabled': !day.enabled }">
              <v-card-subtitle class="pb-2">
                <v-row align="center" no-gutters>
                  <v-col>
                    <v-switch
                      v-model="day.enabled"
                      :label="day.name"
                      color="primary"
                      hide-details
                      dense
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-card-subtitle>

              <v-card-text class="pt-0" v-if="day.enabled">
                <!-- Working Hours -->
                <v-row dense>
                  <v-col cols="6">
                    <v-select
                      v-model="day.startTime"
                      :items="timeOptions"
                      item-text="label"
                      item-value="value"
                      label="Start Time"
                      outlined
                      dense
                      hide-details="auto"
                      prepend-inner-icon="mdi-clock-start"
                    ></v-select>
                  </v-col>
                  <v-col cols="6">
                    <v-select
                      v-model="day.endTime"
                      :items="timeOptions"
                      item-text="label"
                      item-value="value"
                      label="End Time"
                      outlined
                      dense
                      hide-details="auto"
                      prepend-inner-icon="mdi-clock-end"
                    ></v-select>
                  </v-col>
                </v-row>

                <!-- Lunch Break -->
                <v-row dense class="mt-2">
                  <v-col cols="12">
                    <v-switch
                      v-model="day.hasLunch"
                      label="Lunch Break"
                      color="orange lighten-1"
                      hide-details
                      dense
                    ></v-switch>
                  </v-col>
                </v-row>

                <v-row dense v-if="day.hasLunch">
                  <v-col cols="6">
                    <v-select
                      v-model="day.lunchStart"
                      :items="timeOptions"
                      item-text="label"
                      item-value="value"
                      label="Lunch Start"
                      outlined
                      dense
                      hide-details="auto"
                      prepend-inner-icon="mdi-food"
                    ></v-select>
                  </v-col>
                  <v-col cols="6">
                    <v-select
                      v-model="day.lunchEnd"
                      :items="timeOptions"
                      item-text="label"
                      item-value="value"
                      label="Lunch End"
                      outlined
                      dense
                      hide-details="auto"
                      prepend-inner-icon="mdi-food-off"
                    ></v-select>
                  </v-col>
                </v-row>

                <!-- Working Hours Summary -->
                <v-row dense class="mt-3">
                  <v-col cols="12">
                    <v-chip
                      small
                      color="info"
                      text-color="white"
                      v-if="getWorkingHours(day)"
                    >
                      <v-icon left small>mdi-timer</v-icon>
                      {{ getWorkingHours(day) }} hours
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Quick Actions -->
        <!--
        <v-row class="mt-4">
          <v-col cols="12">
            <v-btn-toggle v-model="quickAction" mandatory class="mb-3">
              <v-btn small @click="applyToWeekdays">
                <v-icon left>mdi-calendar-week</v-icon>
                Apply to Weekdays
              </v-btn>
              <v-btn small @click="applyToWeekend">
                <v-icon left>mdi-calendar-weekend</v-icon>
                Apply to Weekend
              </v-btn>
              <v-btn small @click="applyToAll">
                <v-icon left>mdi-calendar</v-icon>
                Apply to All
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
    -->

        <!-- Action Buttons -->
        <v-row class="mt-6">
          <v-col cols="12" class="text-center">
            <v-btn color="success" large @click="saveSettings" class="mr-3">
              <v-icon left>mdi-content-save</v-icon>
              Save Settings
            </v-btn>
            <v-btn color="secondary" large @click="resetSettings">
              <v-icon left>mdi-refresh</v-icon>
              Reset to Default
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="3000"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapActions } from "vuex";
import {
  M_OU,
  SAVE_ADI_DIARY_AVAILABILITY,
  GET_ADI_DIARY_AVAILABILITY,
} from "@/store/mutation-list";

export default {
  name: "CalendarHours",
  props: ["adi_id"],

  data() {
    return {
      quickAction: null,

      // Global Diary Start Time
      globalDiaryStart: "09:30",

      // Time options from 12am to midnight every 30 minutes
      timeOptions: this.generateTimeOptions(),

      // Working Hours Data
      workingHours: [],

      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },

  computed: {
    enabledDaysCount() {
      return this.workingHours.filter((day) => day.enabled).length;
    },

    totalWeeklyHours() {
      return this.workingHours
        .filter((day) => day.enabled)
        .reduce((total, day) => {
          const hours = parseFloat(this.getWorkingHours(day) || 0);
          return total + hours;
        }, 0)
        .toFixed(1);
    },
  },

  methods: {
    ...mapActions({
      saveAvailability: `${M_OU}${SAVE_ADI_DIARY_AVAILABILITY}`,
      getCurrentAvailability: `${M_OU}${GET_ADI_DIARY_AVAILABILITY}`,
    }),
    // Generate time options from 12am to midnight every 30 minutes
    generateTimeOptions() {
      const options = [];

      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const timeValue = `${hour.toString().padStart(2, "0")}:${minute
            .toString()
            .padStart(2, "0")}`;
          const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
          const period = hour < 12 ? "AM" : "PM";
          const minuteStr = minute === 0 ? "00" : minute.toString();

          options.push({
            value: timeValue,
            label: `${displayHour}:${minuteStr} ${period}`,
          });
        }
      }

      return options;
    },

    // Format time for display (convert 24hr to 12hr format)
    formatTimeDisplay(time24) {
      if (!time24) return "";

      const [hour, minute] = time24.split(":").map(Number);
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const period = hour < 12 ? "AM" : "PM";
      const minuteStr = minute.toString().padStart(2, "0");

      return `${displayHour}:${minuteStr} ${period}`;
    },

    // Convert time to minutes for calculations
    timeToMinutes(time) {
      if (!time) return 0;
      const [hours, minutes] = time.split(":").map(Number);
      return hours * 60 + minutes;
    },

    // Working Hours Methods
    getWorkingHours(day) {
      if (!day.enabled || !day.startTime || !day.endTime) return null;

      const start = this.timeToMinutes(day.startTime);
      const end = this.timeToMinutes(day.endTime);
      let totalMinutes = end - start;

      // Handle overnight shifts
      if (end < start) {
        totalMinutes = 24 * 60 - start + end;
      }

      if (day.hasLunch && day.lunchStart && day.lunchEnd) {
        const lunchStart = this.timeToMinutes(day.lunchStart);
        const lunchEnd = this.timeToMinutes(day.lunchEnd);
        let lunchDuration = lunchEnd - lunchStart;

        // Handle overnight lunch breaks
        if (lunchEnd < lunchStart) {
          lunchDuration = 24 * 60 - lunchStart + lunchEnd;
        }

        totalMinutes -= lunchDuration;
      }

      return (totalMinutes / 60).toFixed(1);
    },
    /*
    applyToWeekdays() {
      const template = this.workingHours[0]; // Use Monday as template
      for (let i = 0; i < 5; i++) {
        this.workingHours[i] = {
          ...this.workingHours[i],
          startTime: template.startTime,
          endTime: template.endTime,
          hasLunch: template.hasLunch,
          lunchStart: template.lunchStart,
          lunchEnd: template.lunchEnd,
        };
      }
      this.showSnackbar("Applied settings to weekdays", "success");
    },

    applyToWeekend() {
      const template = this.workingHours[5]; // Use Saturday as template
      for (let i = 5; i < 7; i++) {
        this.workingHours[i] = {
          ...this.workingHours[i],
          startTime: template.startTime,
          endTime: template.endTime,
          hasLunch: template.hasLunch,
          lunchStart: template.lunchStart,
          lunchEnd: template.lunchEnd,
        };
      }
      this.showSnackbar("Applied settings to weekend", "success");
    },

    applyToAll() {
      const template = this.workingHours[0]; // Use Monday as template
      for (let i = 1; i < 7; i++) {
        this.workingHours[i] = {
          ...this.workingHours[i],
          startTime: template.startTime,
          endTime: template.endTime,
          hasLunch: template.hasLunch,
          lunchStart: template.lunchStart,
          lunchEnd: template.lunchEnd,
        };
      }
      this.showSnackbar("Applied settings to all days", "success");
    },
    */
    getDefaultWorkingHours() {
      return [
        {
          name: "Monday",
          enabled: true,
          startTime: "09:00",
          endTime: "17:00",
          hasLunch: true,
          lunchStart: "12:00",
          lunchEnd: "13:00",
        },
        {
          name: "Tuesday",
          enabled: true,
          startTime: "09:00",
          endTime: "17:00",
          hasLunch: true,
          lunchStart: "12:00",
          lunchEnd: "13:00",
        },
        {
          name: "Wednesday",
          enabled: true,
          startTime: "09:00",
          endTime: "17:00",
          hasLunch: true,
          lunchStart: "12:00",
          lunchEnd: "13:00",
        },
        {
          name: "Thursday",
          enabled: true,
          startTime: "09:00",
          endTime: "17:00",
          hasLunch: true,
          lunchStart: "12:00",
          lunchEnd: "13:00",
        },
        {
          name: "Friday",
          enabled: true,
          startTime: "09:00",
          endTime: "17:00",
          hasLunch: true,
          lunchStart: "12:00",
          lunchEnd: "13:00",
        },
        {
          name: "Saturday",
          enabled: false,
          startTime: "10:00",
          endTime: "14:00",
          hasLunch: false,
          lunchStart: "",
          lunchEnd: "",
        },
        {
          name: "Sunday",
          enabled: false,
          startTime: "10:00",
          endTime: "14:00",
          hasLunch: false,
          lunchStart: "",
          lunchEnd: "",
        },
      ];
    },

    // General Methods
    saveSettings() {
      // lets convert the availability to JSON array
      // the list is array of objects
      let temp = [];
      this.workingHours.forEach((element) => {
        let dayOfTheWeek = 0;
        switch (element.name) {
          case "Monday":
            dayOfTheWeek = 1;
            break;
          case "Tuesday":
            dayOfTheWeek = 2;
            break;
          case "Wednesday":
            dayOfTheWeek = 3;
            break;
          case "Thursday":
            dayOfTheWeek = 4;
            break;
          case "Friday":
            dayOfTheWeek = 5;
            break;
          case "Saturday":
            dayOfTheWeek = 6;
            break;
          default:
            dayOfTheWeek = 0;
            break;
        }
        //some temp values
        let is_working = 0,
          work_start = "",
          work_end = "",
          has_lunch = 0,
          lunch_start = "",
          lunch_end = "";

        if (element.enabled) {
          is_working = 1;
          work_start = element.startTime;
          work_end = element.endTime;
        }
        if (element.hasLunch) {
          has_lunch = 1;
          lunch_start = element.lunchStart;
          lunch_end = element.lunchEnd;
        }

        temp.push({
          day_num: dayOfTheWeek,
          is_working: is_working,
          work_start: work_start,
          work_end: work_end,
          has_lunch: has_lunch,
          lunch_start: lunch_start,
          lunch_end: lunch_end,
        });
      });
      let da = JSON.stringify(temp);
      this.saveAvailability({
        adi: this.adi_id,
        availability: da,
      }).then((res) => {
        //response from store. All complete just need to show to popup message
        //console.log(res);
        if (res == "SUCCESS") {
          this.$fire({
            title: "Success",
            text: "Instructor availability saved",
            type: "success",
            timer: 3000,
          }).then((r) => {
            console.log(r.value);
            //clear all and reloadclear()
            this.clear();
            this.$router.go();
          });
        } else {
          this.$fire({
            title: "Error",
            text: "Error saving instructor availability. Please try again.",
            type: "error",
            allowOutsideClick: false,
            //timer: 3000,
          }).then((r) => {
            console.log(r.value);
          });
        }
      });

      const settings = {
        workingHours: this.workingHours,
        globalDiaryStart: this.globalDiaryStart,
      };

      this.$emit("settings-saved", settings);
      this.showSnackbar("Working hours saved successfully!", "success");
    },
    loadCurrentAvailability() {
      this.getCurrentAvailability({ adiid: this.adi_id }).then((rep) => {
        if (rep) {
          //console.log(rep);
          let repData = rep;
          if (Object.hasOwn(repData, "result")) {
            //result propery means ther is an erro
            //do nothting
          } else {
            //no error but empty array
            //lets handle that
            if (repData.length == 0) {
              this.workingHours = this.getDefaultWorkingHours();
            } else {
              // we got the data lets full this up
              //loop throught data
              repData.forEach((record) => {
                let dayOfTheWeek = record.day_num;
                let tempObj = {
                  name: "Sunday",
                  enabled: false,
                  startTime: "10:00",
                  endTime: "14:00",
                  hasLunch: false,
                  lunchStart: "",
                  lunchEnd: "",
                };
                //filling the name to day of the week number
                switch (dayOfTheWeek) {
                  case 1:
                    tempObj.name = "Monday";
                    break;
                  case 2:
                    tempObj.name = "Tuesday";
                    break;
                  case 3:
                    tempObj.name = "Wednesday";
                    break;
                  case 4:
                    tempObj.name = "Thursday";
                    break;
                  case 5:
                    tempObj.name = "Friday";
                    break;
                  case 6:
                    tempObj.name = "Saturday";
                    break;
                  default:
                    tempObj.name = "Sunday";
                    break;
                }
                // filling is workday
                if (record.is_working == 1) {
                  tempObj.enabled = true;
                } else {
                  tempObj.enabled = false;
                }

                //filling start and end times
                tempObj.startTime = record.work_start;
                tempObj.endTime = record.work_end;

                //filling lunch details
                if (record.has_lunch == 1) {
                  tempObj.hasLunch = true;
                } else {
                  tempObj.hasLunch = false;
                }
                tempObj.lunchStart = record.lunch_start;
                tempObj.lunchEnd = record.lunch_end;

                // after end of the loop push the data to main array
                this.workingHours.push(tempObj);
              });
            }
          }
        } //end if
      });
    },

    resetSettings() {
      this.workingHours.forEach((day) => {
        day.enabled = [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
        ].includes(day.name);
        day.startTime = "09:00";
        day.endTime = "17:00";
        day.hasLunch = day.enabled;
        day.lunchStart = "12:00";
        day.lunchEnd = "13:00";
      });

      this.globalDiaryStart = "09:30";
      this.showSnackbar("Settings reset to default", "info");
    },

    showSnackbar(message, color = "success") {
      this.snackbar = {
        show: true,
        message,
        color,
      };
    },
  },

  mounted() {
    //this.loadSettings();
    this.loadCurrentAvailability();
  },
};
</script>

<style scoped>
.day-card {
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.day-card:hover {
  border-color: #1976d2;
}

.day-disabled {
  opacity: 0.6;
  background-color: #f5f5f5;
}

.v-card-subtitle {
  font-weight: 600;
  color: #1976d2 !important;
}

.v-chip {
  margin: 2px;
}

.text-caption {
  font-size: 0.75rem !important;
}

.font-weight-medium {
  font-weight: 500 !important;
}

.text-body-2 div {
  margin-bottom: 4px;
}

.text-body-2 div:last-child {
  margin-bottom: 0;
}

.day-card .v-card-text {
  min-height: 200px;
}

.v-select .v-select__selections {
  font-weight: 500;
}

@media (max-width: 960px) {
  .day-card .v-card-text {
    min-height: auto;
  }
}

.day-card {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 600px) {
  .v-container {
    padding: 8px;
  }

  .v-card-text {
    padding: 16px !important;
  }
}
</style>
